import '../../styles/CommentsBox.css';

import React, {
  useEffect,
  useState,
} from 'react';

import { Typography } from './index';

interface CommentsBoxProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  onBlur?: () => void;
  compact?: boolean;
  onSave?: (value: string) => Promise<void> | void;
}

export default function CommentsBox({ 
  value, 
  onChange, 
  placeholder = "Enter comments...",
  onBlur,
  compact = false,
  onSave
}: CommentsBoxProps) {
  const [localValue, setLocalValue] = useState(value);
  const [dirty, setDirty] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Sync when external value changes (e.g., athlete switch)
  useEffect(() => {
    if (value !== localValue) {
      setLocalValue(value);
      setDirty(false);
      setJustSaved(false);
    }
  }, [value]);
  
  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    setLocalValue(newValue);
    onChange(newValue);
    setDirty(true);
  };
  
  const handleBlur = () => {
    const maybeSave = async () => {
      if (onSave && dirty && !saving) {
        try {
          setSaving(true);
          await onSave(localValue);
          setDirty(false);
          setJustSaved(true);
          setTimeout(() => setJustSaved(false), 1500);
        } finally {
          setSaving(false);
        }
      }
    };
    void maybeSave();
    if (onBlur) onBlur();
  };

  const handleSave = async () => {
    if (!onSave) return;
    try {
      setSaving(true);
      await onSave(localValue);
      setDirty(false);
      setJustSaved(true);
      setTimeout(() => setJustSaved(false), 1500);
    } finally {
      setSaving(false);
    }
  };
  
  const isDirty = dirty;
  
  return (
    <div className={`comments-box ${compact ? 'comments-box-compact' : ''}`}>
      {!compact && <Typography variant="h3">Comments</Typography>}
      <textarea
        value={localValue}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder={placeholder}
        className={`comments-textarea ${compact ? 'comments-textarea-compact' : ''}`}
        rows={compact ? 2 : 5}
      />
      <div className="comments-actions">
        {isDirty && onSave && (
          <button
            type="button"
            className="comments-save-button"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        )}
        {!isDirty && justSaved && (
          <span className="comments-saved-indicator">Saved!</span>
        )}
      </div>
    </div>
  );
}